import React, { useState, useEffect, useCallback } from 'react';
import { settingsApi, SystemSetting } from '../../api/settingsApi';
import { syncApi, SyncStatus, SyncResult, SwipeSyncStatus, LeaveSyncStatus } from '../../api/syncApi';
import { parseAsUTC } from '../../utils/helpers';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Icons } from '../../components/common/Icons';
import './Settings.css';

// Group settings by category
const SETTING_CATEGORIES = {
  'Timesheet': ['timesheet_lock_hours', 'max_hours_per_day', 'min_hours_per_entry', 'hour_increments', 'require_description', 'allow_weekend_entry', 'working_days'],
  'Integrations': ['greythr_token', 'greythr_sync_schedule', 'greythr_sync_enabled', 'greythr_employee_sync_enabled', 'greythr_leave_sync_enabled', 'greythr_swipe_sync_enabled'],
};

// Settings that should be rendered as password fields
const PASSWORD_SETTINGS = ['greythr_token'];

// Days of the week for working days selector
const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Mon' },
  { key: 'tuesday', label: 'Tue' },
  { key: 'wednesday', label: 'Wed' },
  { key: 'thursday', label: 'Thu' },
  { key: 'friday', label: 'Fri' },
  { key: 'saturday', label: 'Sat' },
  { key: 'sunday', label: 'Sun' },
];

export function Settings() {
  const [settings, setSettings] = useState<SystemSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Track modified values
  const [modifiedValues, setModifiedValues] = useState<Record<string, any>>({});
  const [hasChanges, setHasChanges] = useState(false);
  
  // Track password visibility for sensitive fields
  const [passwordVisible, setPasswordVisible] = useState<Record<string, boolean>>({});
  
  // Sync state
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  
  // Swipe sync state
  const [swipeSyncStatus, setSwipeSyncStatus] = useState<SwipeSyncStatus | null>(null);
  const [swipeSyncing, setSwipeSyncing] = useState(false);
  
  // Leave sync state
  const [leaveSyncStatus, setLeaveSyncStatus] = useState<LeaveSyncStatus | null>(null);
  const [leaveSyncing, setLeaveSyncing] = useState(false);
  const [processingLeaves, setProcessingLeaves] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await settingsApi.getSettings();
      setSettings(response.settings);
      
      // Initialize modified values with current values
      const initial: Record<string, any> = {};
      response.settings.forEach(s => {
        initial[s.key] = s.value;
      });
      setModifiedValues(initial);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Initialize default settings if needed
  const handleInitialize = async () => {
    try {
      setLoading(true);
      const response = await settingsApi.initializeSettings();
      if (response.initialized.length > 0) {
        setSuccess(`Initialized ${response.initialized.length} default settings`);
      } else {
        setSuccess('All settings already exist');
      }
      await fetchSettings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initialize settings');
    } finally {
      setLoading(false);
    }
  };

  // Import GreytHR settings from environment variables
  const handleSeedGreythr = async () => {
    try {
      setLoading(true);
      const response = await settingsApi.seedGreythrSettings();
      if (response.updated.length > 0) {
        setSuccess(`Imported ${response.updated.length} GreytHR settings from environment`);
      } else {
        setSuccess('No GreytHR settings found in environment variables');
      }
      await fetchSettings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import GreytHR settings');
    } finally {
      setLoading(false);
    }
  };

  // Fetch sync status
  const fetchSyncStatus = useCallback(async () => {
    try {
      const status = await syncApi.getSyncStatus();
      setSyncStatus(status);
    } catch (err) {
      console.error('Failed to fetch sync status:', err);
    }
  }, []);

  // Fetch swipe sync status
  const fetchSwipeSyncStatus = useCallback(async () => {
    try {
      const status = await syncApi.getSwipeSyncStatus();
      setSwipeSyncStatus(status);
    } catch (err) {
      console.error('Failed to fetch swipe sync status:', err);
    }
  }, []);

  // Fetch leave sync status
  const fetchLeaveSyncStatus = useCallback(async () => {
    try {
      const status = await syncApi.getLeaveSyncStatus();
      setLeaveSyncStatus(status);
    } catch (err) {
      console.error('Failed to fetch leave sync status:', err);
    }
  }, []);

  useEffect(() => {
    fetchSyncStatus();
    fetchSwipeSyncStatus();
    fetchLeaveSyncStatus();
  }, [fetchSyncStatus, fetchSwipeSyncStatus, fetchLeaveSyncStatus]);

  // Test GreytHR connection
  const handleTestConnection = async () => {
    try {
      setTestingConnection(true);
      setError(null);
      const result = await syncApi.testConnection();
      if (result.success) {
        setSuccess(`Connection successful! Host: ${result.host}`);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection test failed');
    } finally {
      setTestingConnection(false);
    }
  };

  // Trigger GreytHR sync
  const handleSync = async () => {
    try {
      setSyncing(true);
      setError(null);
      const response = await syncApi.syncGreythrEmployees();
      
      if (response.results.success) {
        setSuccess(
          `Sync completed! Created: ${response.results.created}, Updated: ${response.results.updated}, Deactivated: ${response.results.deactivated}`
        );
      } else {
        setError(`Sync completed with errors: ${response.results.errors.join(', ')}`);
      }
      
      await fetchSyncStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  // Trigger Swipe/Attendance sync
  const handleSwipeSync = async () => {
    try {
      setSwipeSyncing(true);
      setError(null);
      const response = await syncApi.syncSwipes();
      
      if (response.results.success) {
        setSuccess(
          `Swipe sync completed! Employees: ${response.results.employees_processed}, Created: ${response.results.total_created}`
        );
      } else {
        setError(`Swipe sync had errors: ${response.results.errors.slice(0, 3).join(', ')}`);
      }
      
      await fetchSwipeSyncStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Swipe sync failed');
    } finally {
      setSwipeSyncing(false);
    }
  };

  // Trigger Leave sync
  const handleLeaveSync = async () => {
    try {
      setLeaveSyncing(true);
      setError(null);
      const response = await syncApi.syncLeaves();
      
      if (response.results.success) {
        setSuccess(
          `Leave sync completed! Created: ${response.results.created}, Updated: ${response.results.updated}`
        );
      } else {
        setError(`Leave sync had errors: ${response.results.errors.slice(0, 3).join(', ')}`);
      }
      
      await fetchLeaveSyncStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Leave sync failed');
    } finally {
      setLeaveSyncing(false);
    }
  };

  // Process leave timesheets
  const handleProcessLeaveTimesheets = async () => {
    try {
      setProcessingLeaves(true);
      setError(null);
      const response = await syncApi.processLeaveTimesheets();
      
      setSuccess(
        `Leave processing completed! Processed: ${response.results.processed}, Timesheets: ${response.results.timesheets_created}`
      );
      
      await fetchLeaveSyncStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Leave processing failed');
    } finally {
      setProcessingLeaves(false);
    }
  };

  const handleValueChange = (key: string, value: any) => {
    setModifiedValues(prev => ({
      ...prev,
      [key]: value,
    }));
    
    // Check if value differs from original
    const setting = settings.find(s => s.key === key);
    const originalMatch = setting && JSON.stringify(setting.value) === JSON.stringify(value);
    
    if (!originalMatch) {
      setHasChanges(true);
    } else {
      // Recheck all values
      const anyChanges = settings.some(s => 
        JSON.stringify(modifiedValues[s.key]) !== JSON.stringify(s.value)
      );
      setHasChanges(anyChanges);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      const changedSettings = settings
        .filter(s => JSON.stringify(modifiedValues[s.key]) !== JSON.stringify(s.value))
        .map(s => ({ key: s.key, value: modifiedValues[s.key] }));

      if (changedSettings.length === 0) {
        setError('No changes to save');
        setSaving(false);
        return;
      }

      await settingsApi.updateSettingsBatch({ settings: changedSettings });
      setSuccess(`Saved ${changedSettings.length} setting(s)`);
      setHasChanges(false);
      await fetchSettings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    const initial: Record<string, any> = {};
    settings.forEach(s => {
      initial[s.key] = s.value;
    });
    setModifiedValues(initial);
    setHasChanges(false);
  };

  const renderSettingInput = (setting: SystemSetting) => {
    const value = modifiedValues[setting.key] ?? setting.value;

    // Special handling for working_days
    if (setting.key === 'working_days' || setting.key.includes('working_days')) {
      const workingDays = Array.isArray(value) ? value : [];
      return (
        <div className="working-days-selector">
          {DAYS_OF_WEEK.map(day => (
            <label key={day.key} className={`day-checkbox ${workingDays.includes(day.key) ? 'checked' : ''}`}>
              <input
                type="checkbox"
                checked={workingDays.includes(day.key)}
                onChange={(e) => {
                  const newDays = e.target.checked
                    ? [...workingDays, day.key]
                    : workingDays.filter((d: string) => d !== day.key);
                  handleValueChange(setting.key, newDays);
                }}
              />
              <span>{day.label}</span>
            </label>
          ))}
        </div>
      );
    }

    switch (setting.data_type) {
      case 'boolean':
        return (
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={Boolean(value)}
              onChange={(e) => handleValueChange(setting.key, e.target.checked)}
            />
            <span className="slider"></span>
          </label>
        );
      
      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleValueChange(setting.key, Number(e.target.value))}
            className="setting-input"
          />
        );
      
      case 'json':
        return (
          <textarea
            value={typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value);
                handleValueChange(setting.key, parsed);
              } catch {
                // Keep as string while editing
                handleValueChange(setting.key, e.target.value);
              }
            }}
            className="setting-textarea"
            rows={3}
          />
        );
      
      default: // string
        if (setting.key.includes('week_starts_on')) {
          return (
            <select
              value={value}
              onChange={(e) => handleValueChange(setting.key, e.target.value)}
              className="setting-select"
            >
              <option value="sunday">Sunday</option>
              <option value="monday">Monday</option>
              <option value="saturday">Saturday</option>
            </select>
          );
        }
        if (setting.key.includes('fiscal_year_start')) {
          return (
            <select
              value={value}
              onChange={(e) => handleValueChange(setting.key, e.target.value)}
              className="setting-select"
            >
              <option value="january">January</option>
              <option value="april">April</option>
              <option value="july">July</option>
              <option value="october">October</option>
            </select>
          );
        }
        // Handle password/sensitive fields
        if (PASSWORD_SETTINGS.includes(setting.key)) {
          const isVisible = passwordVisible[setting.key] || false;
          return (
            <div className="password-input-wrapper">
              <input
                type={isVisible ? "text" : "password"}
                value={value}
                onChange={(e) => handleValueChange(setting.key, e.target.value)}
                className="setting-input"
                placeholder="Enter value..."
                autoComplete="new-password"
              />
              <button
                type="button"
                className="toggle-password-btn"
                onClick={() => setPasswordVisible(prev => ({
                  ...prev,
                  [setting.key]: !prev[setting.key]
                }))}
                title={isVisible ? "Hide" : "Show"}
              >
                {isVisible ? <Icons.EyeOff /> : <Icons.Eye />}
              </button>
            </div>
          );
        }
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleValueChange(setting.key, e.target.value)}
            className="setting-input"
          />
        );
    }
  };

  const getSettingsByCategory = (category: string) => {
    const keys = SETTING_CATEGORIES[category as keyof typeof SETTING_CATEGORIES] || [];
    return settings.filter(s => keys.includes(s.key));
  };

  const getUncategorizedSettings = () => {
    const allCategorizedKeys = Object.values(SETTING_CATEGORIES).flat();
    return settings.filter(s => !allCategorizedKeys.includes(s.key));
  };

  return (
    <div className="settings-page">
      <header className="settings-header">
        <div className="settings-title">
          <h1>System Settings</h1>
          <p>Configure application settings and preferences</p>
        </div>
        <div className="settings-actions">
          <Button variant="secondary" onClick={handleReset} disabled={!hasChanges}>
            Reset
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving || !hasChanges}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </header>

      {error && (
        <div className="alert alert-error">
          <Icons.AlertCircle />
          <span>{error}</span>
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <Icons.Check />
          <span>{success}</span>
          <button onClick={() => setSuccess(null)}>&times;</button>
        </div>
      )}

      {settings.length === 0 ? (
        <Card>
          <div className="empty-state">
            <Icons.Settings />
            <h3>No Settings Found</h3>
            <p>Click "Initialize Defaults" to create the default settings.</p>
            <Button variant="primary" onClick={handleInitialize}>
              Initialize Default Settings
            </Button>
          </div>
        </Card>
      ) : (
        <div className="settings-grid">
          {Object.keys(SETTING_CATEGORIES).map((category) => {
            const categorySettings = getSettingsByCategory(category);
            if (categorySettings.length === 0) return null;

            return (
              <Card key={category} className="settings-card">
                <div className="card-header-row">
                  <h2 className="card-title">{category}</h2>
                  {category === 'Integrations' && (
                    <Button 
                      variant="secondary" 
                      onClick={handleSeedGreythr}
                      className="import-btn"
                      title="Import GreytHR credentials from server environment"
                    >
                      Import from Env
                    </Button>
                  )}
                </div>
                <div className="settings-list">
                  {categorySettings.map((setting) => (
                    <div key={setting.key} className="setting-item">
                      <div className="setting-info">
                        <label className="setting-label">
                          {setting.key.replace(/_/g, ' ')}
                        </label>
                        {setting.description && (
                          <p className="setting-description">{setting.description}</p>
                        )}
                      </div>
                      <div className="setting-control">
                        {renderSettingInput(setting)}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* GreytHR Sync Controls - Only show in Integrations */}
                {category === 'Integrations' && (
                  <>
                    {/* Employee Sync Section */}
                    <div className="sync-section">
                      <h3 className="sync-title">GreytHR Employee Sync</h3>
                      
                      {syncStatus && (
                        <div className="sync-status">
                          <div className="sync-status-row">
                            <span className="sync-label">Status:</span>
                            <span className={`sync-value ${syncStatus.ready ? 'ready' : 'not-ready'}`}>
                              {syncStatus.ready ? 'Ready' : 'Not Configured'}
                            </span>
                          </div>
                          {syncStatus.last_sync && (
                            <div className="sync-status-row">
                              <span className="sync-label">Last Sync:</span>
                              <span className="sync-value">
                                {parseAsUTC(syncStatus.last_sync).toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}
                              </span>
                            </div>
                          )}
                          {syncStatus.last_result && (
                            <div className="sync-status-row">
                              <span className="sync-label">Last Result:</span>
                              <span className="sync-value">
                                Created: {syncStatus.last_result.created}, 
                                Updated: {syncStatus.last_result.updated}, 
                                Deactivated: {syncStatus.last_result.deactivated}
                              </span>
                            </div>
                          )}
                          <div className="sync-status-row">
                            <span className="sync-label">Schedule:</span>
                            <span className="sync-value">
                              Daily at {syncStatus.sync_schedule || '10:00'} IST
                              {!syncStatus.sync_enabled && ' (Disabled)'}
                            </span>
                          </div>
                        </div>
                      )}
                      
                      <div className="sync-actions">
                        <Button
                          variant="secondary"
                          onClick={handleTestConnection}
                          disabled={testingConnection}
                        >
                          {testingConnection ? 'Testing...' : 'Test Connection'}
                        </Button>
                        <Button
                          variant="primary"
                          onClick={handleSync}
                          disabled={syncing || !syncStatus?.ready}
                        >
                          {syncing ? 'Syncing...' : 'Sync Now'}
                        </Button>
                      </div>
                    </div>

                    {/* Attendance/Swipe Sync Section */}
                    <div className="sync-section">
                      <h3 className="sync-title">Attendance Swipe Sync</h3>
                      
                      {swipeSyncStatus && (
                        <div className="sync-status">
                          <div className="sync-status-row">
                            <span className="sync-label">Total Swipes:</span>
                            <span className="sync-value">{swipeSyncStatus.total_swipes.toLocaleString()}</span>
                          </div>
                          <div className="sync-status-row">
                            <span className="sync-label">Today's Swipes:</span>
                            <span className="sync-value">{swipeSyncStatus.today_swipes}</span>
                          </div>
                          <div className="sync-status-row">
                            <span className="sync-label">Employees Tracked:</span>
                            <span className="sync-value">{swipeSyncStatus.employees_tracked}</span>
                          </div>
                          <div className="sync-status-row">
                            <span className="sync-label">Schedule:</span>
                            <span className="sync-value">Every 5 minutes (auto)</span>
                          </div>
                        </div>
                      )}
                      
                      <div className="sync-actions">
                        <Button
                          variant="primary"
                          onClick={handleSwipeSync}
                          disabled={swipeSyncing || !syncStatus?.ready}
                        >
                          {swipeSyncing ? 'Syncing...' : 'Sync Swipes Now'}
                        </Button>
                      </div>
                    </div>

                    {/* Leave Sync Section */}
                    <div className="sync-section">
                      <h3 className="sync-title">Leave Sync & Auto-Fill</h3>
                      
                      {leaveSyncStatus && (
                        <div className="sync-status">
                          <div className="sync-status-row">
                            <span className="sync-label">Total Leaves:</span>
                            <span className="sync-value">{leaveSyncStatus.total_leaves}</span>
                          </div>
                          <div className="sync-status-row">
                            <span className="sync-label">Pending Fill:</span>
                            <span className="sync-value">{leaveSyncStatus.pending_leaves}</span>
                          </div>
                          <div className="sync-status-row">
                            <span className="sync-label">Timesheets Filled:</span>
                            <span className="sync-value">{leaveSyncStatus.filled_leaves}</span>
                          </div>
                          <div className="sync-status-row">
                            <span className="sync-label">Schedule:</span>
                            <span className="sync-value">Daily at 19:00 IST (auto)</span>
                          </div>
                        </div>
                      )}
                      
                      <div className="sync-actions">
                        <Button
                          variant="secondary"
                          onClick={handleLeaveSync}
                          disabled={leaveSyncing || !syncStatus?.ready}
                        >
                          {leaveSyncing ? 'Syncing...' : 'Sync Leaves'}
                        </Button>
                        <Button
                          variant="primary"
                          onClick={handleProcessLeaveTimesheets}
                          disabled={processingLeaves || !syncStatus?.ready}
                        >
                          {processingLeaves ? 'Processing...' : 'Fill Timesheets'}
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </Card>
            );
          })}

          {/* Uncategorized settings */}
          {getUncategorizedSettings().length > 0 && (
            <Card className="settings-card">
              <h2 className="card-title">Other</h2>
              <div className="settings-list">
                {getUncategorizedSettings().map((setting) => (
                  <div key={setting.key} className="setting-item">
                    <div className="setting-info">
                      <label className="setting-label">{setting.key}</label>
                      {setting.description && (
                        <p className="setting-description">{setting.description}</p>
                      )}
                    </div>
                    <div className="setting-control">
                      {renderSettingInput(setting)}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

export default Settings;
